import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ChangeDetectorRef, NgZone } from '@angular/core';
import { FileService } from 'src/app/services/file/file.service';
import { UploadComponent } from "./upload/upload.component";
import { SpinnerComponent } from "../spinner/spinner.component";
import { FolderCreateComponent } from "./folder-create/folder-create.component";
import { FileEditComponent } from "./file-edit/file-edit.component";

@Component({
  selector: 'app-file-manager',
  imports: [CommonModule, UploadComponent, SpinnerComponent, FolderCreateComponent, FileEditComponent],
  templateUrl: './file-manager.component.html',
  styleUrl: './file-manager.component.css'
})
export class FileManagerComponent {
  @Input() type: 'image' | 'file' = 'image';
  @Input() multiple = false;
  @Output() fileSelected = new EventEmitter<string>();
  @Output() multipleFilesSelected = new EventEmitter<string[]>();
  @Input() allowedFiles: string[] = [];
  @Input() multiselect: boolean = false;

  fileList: string[] = [];
  fileTree: any[] = [];
  pathStack: { path: string }[] = [];
  currentFolderPath = '';
  load = false;
  selectedFiles: string[] = [];

  editingFile: any = null;

  private folderCache = new Map<string, any[]>();

  constructor(
    private FileService: FileService,
    private cd: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngOnInit() {
    this.getFiles('');
  }

  get normalizedCurrentPath(): string {
    return this.normalizePath(this.currentFolderPath);
  }

  normalizePath(path: string): string {
    return (path || '').replace(/\/+/g, '/').replace(/^\/|\/$/g, '');
  }

  trackByPath(index: number, item: any): string {
    return item?.path || item?.url || `${item?.name}-${index}`;
  }

  private getAllowedFileNames(): string[] {
    return this.allowedFiles.map(f => {
      const clean = f.split('?')[0];
      return clean.substring(clean.lastIndexOf('/') + 1);
    });
  }

  getFiles(path: string = '', forceReload = false) {
    const normalizedPath = this.normalizePath(path);

    if (!forceReload && this.folderCache.has(normalizedPath)) {
      this.fileTree = this.folderCache.get(normalizedPath)!;
      this.currentFolderPath = normalizedPath;
      return;
    }

    this.load = true;

    this.FileService.getFiles(normalizedPath).subscribe(
      (data: any) => {
        this.zone.run(() => {
          let finalData = Array.isArray(data) ? data : [];

          if (this.allowedFiles?.length) {
            const allowedNames = this.getAllowedFileNames();
            finalData = finalData.filter(item =>
              item.type === 'file' && allowedNames.includes(item.name)
            );
          }

          this.fileTree = finalData;
          this.currentFolderPath = normalizedPath;
          this.folderCache.set(normalizedPath, finalData);

          this.load = false;
          this.cd.detectChanges();
        });
      },
      (error: any) => {
        console.log(error);
        this.zone.run(() => {
          this.load = false;
          this.cd.detectChanges();
        });
      }
    );
  }

  onUploadComplete() {
    this.folderCache.delete(this.normalizedCurrentPath);
    this.getFiles(this.normalizedCurrentPath, true);
  }

  selectFile(file: string) {
    if (this.multiselect) {
      this.selectMultipleFile(file);
    } else {
      this.fileSelected.emit(file);
    }
  }

  selectMultipleFile(file: string) {
    const index = this.selectedFiles.indexOf(file);

    if (index === -1) {
      this.selectedFiles.push(file);
    } else {
      this.selectedFiles.splice(index, 1);
    }

    this.multipleFilesSelected.emit([...this.selectedFiles]);
  }

  isImageFile(name: string): boolean {
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(name);
  }

  isDocumentFile(name: string): boolean {
    return /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt)$/i.test(name);
  }

  openFolder(folder: any) {
    this.pathStack.push({ path: this.normalizedCurrentPath });
    const nextPath = this.normalizePath(
      this.normalizedCurrentPath
        ? `${this.normalizedCurrentPath}/${folder.name}`
        : folder.name
    );
    this.getFiles(nextPath);
  }

  goBack() {
    if (this.pathStack.length > 0) {
      const previous = this.pathStack.pop()!;
      this.getFiles(previous.path);
    }
  }

  openInNewTab(item: any) {
    if (item?.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    }
  }

  delete(item: any) {
    if (!confirm(`დარწმუნებული ხარ რომ გინდა წაშალო "${item.name}"?`)) return;

    this.load = true;

    const payload = {
      path: item.path,
      type: item.type,
      source: item.source || 'public'
    };

    this.FileService.deleteItem(payload).subscribe(
      () => {
        this.folderCache.delete(this.normalizedCurrentPath);
        this.getFiles(this.normalizedCurrentPath, true);
      },
      (error: any) => {
        console.error('შეცდომა წაშლის დროს:', error);
        this.load = false;
      }
    );
  }

  copyLink(item: any) {
    if (!item?.url) return;

    navigator.clipboard.writeText(item.url);
    alert('ლინკი დაკოპირდა');
  }

  openEdit(file: any) {
    this.editingFile = file;
  }

  getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase();

  switch (ext) {
    case 'pdf':
      return 'fi-rr-file-pdf';

    case 'doc':
    case 'docx':
      return 'fi-rr-file-word';

    case 'xls':
    case 'xlsx':
      return 'fi-rr-file-excel';

    case 'ppt':
    case 'pptx':
      return 'fi-rr-file-powerpoint';

    case 'zip':
    case 'rar':
      return 'fi-rr-file-zipper';

    case 'txt':
      return 'fi-rr-file';

    default:
      return 'fi-rr-file';
  }
}
  

}